import { GradientType, Matrix, SpreadMethod, TextField } from "@goodgamestudios/createjs-utilities";
import { int } from "@goodgamestudios/math-utilities";
import { Back, Power4, Sine, TweenLite } from "gsap";
import { SeatVO } from "../../../gamevo/SeatVO";
import { MathUtils } from "../../../util/MathUtils";
import { PokerTextHelper } from "../../helper/PokerTextHelper";
import MovieClip = createjs.MovieClip;
import Container = createjs.Container;
import BlurFilter = createjs.BlurFilter;
import Rectangle = createjs.Rectangle;

export class PlayerStateViewComponent {
    public static GRADIENT_GREY: any[] = [0x808080, 0x333333];
    public static GRADIENT_ORANGE: any[] = [0xFF7D03, 0xC73D02];
    public static GRADIENT_GREEN: any[] = [0x69CC26, 0x417D17];
    public static GRADIENT_BLUE: any[] = [0x5695CF, 0x33587A];
    public static GRADIENT_YELLOW: any[] = [0xFFB700, 0xBA9700];

    private _tweenDuration: number = .3; // .3

    private _width: number = 80;
    private _height: number = 15;

    private _view: MovieClip;
    private _currentState: number = 0;
    private _labelCurrent: MovieClip;
    private _labelNext: MovieClip;
    private _labelCurrentTxt: TextField;
    private _labelNextTxt: TextField;
    private _labelNextStartY: number = NaN;
    private _labelCurrentStartY: number = NaN;
    private _bgHolder: MovieClip;
    private _gt_bgCurrent: TweenLite;
    private _gt_bgNext: TweenLite;
    private _gradientCurrent: Container;
    private _gradientNext: Container;
    private _gt_labelCurrent: TweenLite;
    private _gt_labelNext: TweenLite;
    private _transitionRunning: boolean = false;
    private _updateStateAfterTransition: boolean = false;
    private _lastState: number = 0;
    private _gt_labelNextBlur: TweenLite;
    private _gradientHolder: Container;
    private _label_container: MovieClip;
    private _nextGradient: any[];

    constructor(view: MovieClip) {
        this._view = view;

        this._label_container = this._view.label_container;
        this._label_container.scrollRect = new Rectangle(0, 0, this._width, this._height);
        this._labelCurrent = this._label_container.label_current;
        this._labelNext = this._label_container.label_next;
        this._labelCurrentTxt = this._labelCurrent.txt_label;
        this._labelNextTxt = this._labelNext.txt_label;

        this._labelCurrentStartY = this._labelCurrent.y;
        this._labelNextStartY = -this._labelCurrent.height;
        this._labelNext.y = this._labelNextStartY;

        // bg holder
        this._bgHolder = this._view.bgHolder;
        this._gradientHolder = new Container();
        this._gradientHolder.scrollRect = new Rectangle(0, 0, this._width, this._height);
        this._bgHolder.addChild(this._gradientHolder);

        // mask for round corner
        // var roundCornerMask: Library.PokerTable.PlayerActionStateBg = new Library.PokerTable.PlayerActionStateBg();
        // roundCornerMask.width = 79.65;
        // this._bgHolder.addChild(roundCornerMask);
        let roundCornerMask = new createjs.Shape();
        roundCornerMask.graphics.drawRoundRectComplex(0, 0, 80, 15, 5, 5, 0, 0);
        this._bgHolder.mask = roundCornerMask;

        // init gradients
        this.initGradients();

        // init default state
        this.setState(8);
    }

    private initGradients(): void {
        this._gradientCurrent = new Container();
        this._gradientNext = new Container();

        this._gradientHolder.addChild(this._gradientCurrent);
        this._gradientHolder.addChild(this._gradientNext);
    }

    public setState(stateId: number): void {
        this._currentState = int(stateId);
        this.updateState(false);
        this._lastState = this._currentState;
    }

    public switchState(stateId: number): void {
        this._currentState = int(stateId);
        this.updateState();
        this._lastState = this._currentState;
    }

    private updateState(showTransition: boolean = true): void {
        // special state -1: hide state view
        if (this._currentState == -1) {
            this._view.visible = false;
            return;
        }
        else {
            this._view.visible = true;
        }

        // if state hasn't changed, there's nothing to do
        if (this._lastState == this._currentState) {
            return;
        }

        // prevent multiple transition executions
        if (this._transitionRunning) {
            this._updateStateAfterTransition = true;
            return;
        }

        // set '_transitionRunning' flag only true if a transition is needed
        if (showTransition) {
            this._transitionRunning = true;
        }

        var gradient: any[];
        var textId: string;

        switch (this._currentState) {
            case 8:
            case 8:

                gradient = PlayerStateViewComponent.GRADIENT_GREY;
                textId = "table_playerStatus_waiting";
                break;

            case 3:

                gradient = PlayerStateViewComponent.GRADIENT_BLUE;
                textId = "table_playerStatus_call";
                break;

            case 2:

                gradient = PlayerStateViewComponent.GRADIENT_ORANGE;
                textId = "table_playerStatus_check";
                break;

            case 7:

                gradient = PlayerStateViewComponent.GRADIENT_GREEN;
                textId = "table_playerStatus_thinking";
                break;

            case 1:

                gradient = PlayerStateViewComponent.GRADIENT_GREY;
                textId = "table_playerStatus_fold";
                break;

            case 4:

                gradient = PlayerStateViewComponent.GRADIENT_GREEN;
                textId = "table_playerStatus_raise";
                break;

            case 5:

                gradient = PlayerStateViewComponent.GRADIENT_YELLOW;
                textId = "table_playerStatus_allin";
                break;

            default:

                gradient = PlayerStateViewComponent.GRADIENT_GREY;
                textId = "table_playerStatus_waiting";
        }

        if (showTransition) {
            // switch view of current and next item
            if (this._labelNextTxt.text != "") {
                this._labelCurrentTxt.text = this._labelNextTxt.text;
            }
            if (this._nextGradient) {
                this.drawGradient(this._gradientCurrent, this._nextGradient);
            }

            // update next label
            PokerTextHelper.writeLabel(textId, this._labelNextTxt, 9, true);

            // update next gradient
            this._nextGradient = this.drawGradient(this._gradientNext, gradient);

            // reset positions
            this._labelCurrent.y = 0;
            this._labelCurrent.visible = true;
            this._labelNext.y = -this._height / 3;
            this._labelNext.alpha = 0.1;
            this._gradientCurrent.y = 0;
            this._gradientCurrent.visible = true;
            this._gradientNext.y = -this._height;

            // tween labels
            this._gt_labelCurrent = new TweenLite(this._labelCurrent, this._tweenDuration, { y: this._height, blurY: 6, alpha: 0, ease: Back.easeOut, delay: 0.1, onUpdate: this.bindFunction(this.updateFilter), onUpdateParams: ["{self}"] });
            this._gt_labelCurrent.play();

            // this._labelNext.filters = [new BlurFilter(0, 8)];
            // this._gt_labelNextBlur = new TweenLite(this._labelNext, this._tweenDuration, { blurY: 0, ease: Sine.easeOut, delay: 0.1, onUpdate: this.bindFunction(this.updateFilter), onUpdateParams: ["{self}"] });
            // this._gt_labelNextBlur.play();

            this._gt_labelNext = new TweenLite(this._labelNext, this._tweenDuration, { y: 0, alpha: 1, ease: Back.easeOut, delay: 0.1, onComplete: this.bindFunction(this.onTransitionComplete) });
            this._gt_labelNext.play();

            // tween gradients
            this._gt_bgCurrent = new TweenLite(this._gradientCurrent, this._tweenDuration, { y: this._height, ease: Power4.easeOut });
            this._gt_bgCurrent.play();

            this._gt_bgNext = new TweenLite(this._gradientNext, this._tweenDuration, { y: 0, ease: Power4.easeOut });
            this._gt_bgNext.play();
        }
        else {
            // reset positions
            this._labelCurrent.y = 0;
            this._labelNext.y = -this._height;
            this._gradientCurrent.y = 0;
            this._gradientNext.y = -this._height;

            // show current label
            this._labelCurrentTxt.text = textId;
            PokerTextHelper.writeLabel(textId, this._labelCurrentTxt, 9, true);

            // draw current gradient
            this.drawGradient(this._gradientCurrent, gradient);
        }
    }

    private updateFilter(tweenObj: TweenLite): void {
        // tweenObj.target.uncache();
        // var blurFilter = new BlurFilter(0, 0, 1);
        // if (!tweenObj.target.filters || !tweenObj.target.filters.length) {
        //     blurFilter.blurY = tweenObj.vars.blurY;
        // }
        // else if (tweenObj.target.filters && tweenObj.target.filters.length) {
        //     blurFilter = tweenObj.target.filters[0];
        //     blurFilter.blurY = tweenObj.vars.blurY;
        // }

        // tweenObj.target.useFilters([blurFilter]);
    }

    private onTransitionComplete(tween: TweenLite): void {
        this._transitionRunning = false;

        // this._labelCurrent.filers = [];
        this._gradientCurrent.visible = false;
        this._labelCurrent.visible = false;

        // check if the state has changed while the transition was running
        if (this._updateStateAfterTransition) {
            this._updateStateAfterTransition = false;
            this._lastState = -1;
            this.updateState();
        }
    }

    private drawGradient(target: Container, gradientColors: any[]): any[] {
        var fillType: string = GradientType.LINEAR;
        var colors: any[] = gradientColors;
        var alphas: any[] = [1, 1];
        var ratios: any[] = [0, 255];
        var matr: Matrix = new Matrix();
        matr.createGradientBox(this._width, this._height, MathUtils.degreesToRadians(90), 0, 0);
        var spreadMethod: string = SpreadMethod.PAD;

        target.graphics.beginGradientFill(fillType, colors, alphas, ratios, matr, spreadMethod);
        target.graphics.drawRect(0, 0, this._width, this._height);
        target.graphics.endFill();

        return gradientColors;
    }

    public kill(): void {
        this._gt_labelCurrent = null;
        this._gt_labelNext = null;
        this._gt_bgCurrent = null;
        this._gt_bgNext = null;
        this._gt_labelNextBlur = null;
    }
}
